// src/components/GoogleCalendarImport.tsx

import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/hooks/useProjects';
import { getPlanFeatures } from '@/lib/planFeatures';
import { UpgradeModal } from '@/components/UpgradeModal';
import { Calendar, Download, RefreshCw, Clock, CheckCircle2, Lock } from 'lucide-react';

// ... 既存のインターフェース定義は変更なし ...

const GOOGLE_CLIENT_ID = '858098732942-ju20bsbt07qkfi2hg3v8uoii4jks0lt0.apps.googleusercontent.com';
const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events.readonly';
const TOKEN_STORAGE_KEY = 'google_auth_token';

interface CalendarEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  description?: string;
  calendarId: string;
  calendarName: string;
  selected?: boolean;
}

interface GoogleCalendar {
  id: string;
  summary: string;
  backgroundColor: string;
  selected?: boolean;
}

interface TokenResponse {
  access_token: string;
  expires_in: number;
  expires_at?: number;
  error?: string;
}

export function GoogleCalendarImport() {
  const { user } = useAuth();
  const { projects } = useProjects('active');
  const features = getPlanFeatures(user);
  
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [calendars, setCalendars] = useState<GoogleCalendar[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedCalendars, setSelectedCalendars] = useState<Set<string>>(new Set());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [projectMapping, setProjectMapping] = useState<Record<string, string>>({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Googleカレンダー連携が利用不可の場合
  if (!features.googleCalendarSync) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-blue-600" />
            Googleカレンダー連携
          </h1>
          <p className="text-gray-600 mt-2">
            Googleカレンダーのイベントを稼働記録としてインポートできます
          </p>
        </div>

        {/* プラン制限の案内 */}
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Standardプラン限定機能
          </h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Googleカレンダー連携はStandardプランで利用できます。
            アップグレードして、カレンダーのイベントを簡単にインポートしましょう。
          </p>
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-lg shadow-orange-500/30"
          >
            Standardプランにアップグレード
          </button>
        </div>

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="Googleカレンダー連携"
        />
      </div>
    );
  }

  // 以下、既存のコード（認証済みユーザー向け）
  // useEffect, 各種ハンドラー等は変更なし
  
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);

    checkStoredToken();

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google Identity Services loaded');
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const checkStoredToken = () => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedToken) {
      try {
        const token: TokenResponse = JSON.parse(storedToken);
        if (token.expires_at && token.expires_at > Date.now()) {
          setIsAuthorized(true);
        } else {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error('Failed to parse stored token:', error);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setIsAuthorized(false);
      }
    }
  };

  const handleAuthorize = () => {
    const google = (window as any).google;
    if (!google) {
      alert('Google Identity Services が読み込まれていません。ページをリロードしてください。');
      return;
    }

    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_SCOPES,
      callback: (tokenResponse: TokenResponse) => {
        if (tokenResponse.error) {
          console.error('Authorization error:', tokenResponse.error);
          alert('認証に失敗しました: ' + tokenResponse.error);
          return;
        }
        
        const tokenWithExpiry = {
          ...tokenResponse,
          expires_at: Date.now() + (tokenResponse.expires_in * 1000),
        };
        
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenWithExpiry));
        setIsAuthorized(true);
        alert('Google カレンダーへの接続に成功しました！');
      },
    });

    tokenClient.requestAccessToken({ prompt: 'consent' });
  };

  const handleSignout = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setIsAuthorized(false);
    setCalendars([]);
    setEvents([]);
    setSelectedCalendars(new Set());
    alert('サインアウトしました');
  };

  const getAccessToken = (): string | null => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!storedToken) return null;

    try {
      const token: TokenResponse = JSON.parse(storedToken);
      if (token.expires_at && token.expires_at > Date.now()) {
        return token.access_token;
      } else {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setIsAuthorized(false);
        return null;
      }
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  };

  const fetchCalendarList = async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      alert('認証が必要です。「Googleカレンダーと連携」ボタンをクリックしてください。');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        'https://www.googleapis.com/calendar/v3/users/me/calendarList',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const calendarList: GoogleCalendar[] = data.items.map((item: any) => ({
        id: item.id,
        summary: item.summary || item.id,
        backgroundColor: item.backgroundColor || '#3b82f6',
      }));

      setCalendars(calendarList);
      
      const primaryCalendar = calendarList.find((cal) => cal.id === 'primary');
      if (primaryCalendar) {
        setSelectedCalendars(new Set([primaryCalendar.id]));
      }
    } catch (error) {
      console.error('Failed to fetch calendar list:', error);
      alert('カレンダーリストの取得に失敗しました: ' + error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEvents = async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      alert('認証が必要です');
      return;
    }

    if (selectedCalendars.size === 0) {
      alert('カレンダーを選択してください');
      return;
    }

    if (!startDate || !endDate) {
      alert('期間を指定してください');
      return;
    }

    setIsLoading(true);
    try {
      const timeMin = new Date(startDate).toISOString();
      const timeMax = new Date(endDate + 'T23:59:59').toISOString();

      const allEvents: CalendarEvent[] = [];

      for (const calendarId of Array.from(selectedCalendars)) {
        const calendar = calendars.find((cal) => cal.id === calendarId);
        const calendarName = calendar?.summary || calendarId;

        const response = await fetch(
          `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
            calendarId
          )}/events?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(
            timeMax
          )}&singleEvents=true&orderBy=startTime`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          console.error(`Failed to fetch events for calendar ${calendarId}`);
          continue;
        }

        const data = await response.json();
        const calendarEvents: CalendarEvent[] = (data.items || []).map((item: any) => ({
          id: item.id,
          summary: item.summary || '(タイトルなし)',
          start: item.start.dateTime || item.start.date,
          end: item.end.dateTime || item.end.date,
          description: item.description,
          calendarId,
          calendarName,
        }));

        allEvents.push(...calendarEvents);
      }

      allEvents.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

      setEvents(allEvents);
      
      if (allEvents.length === 0) {
        alert('指定した期間にイベントがありません');
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      alert('イベントの取得に失敗しました: ' + error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalendarToggle = (calendarId: string) => {
    const newSelected = new Set(selectedCalendars);
    if (newSelected.has(calendarId)) {
      newSelected.delete(calendarId);
    } else {
      newSelected.add(calendarId);
    }
    setSelectedCalendars(newSelected);
  };

  const handleEventToggle = (eventId: string) => {
    setEvents((prev) =>
      prev.map((event) =>
        event.id === eventId ? { ...event, selected: !event.selected } : event
      )
    );
  };

  const handleSelectAllEvents = () => {
    const allSelected = events.every((e) => e.selected);
    setEvents((prev) => prev.map((event) => ({ ...event, selected: !allSelected })));
  };

  const handleImport = async () => {
    if (!user) {
      alert('ログインが必要です');
      return;
    }

    const selectedEvents = events.filter((event) => event.selected);

    if (selectedEvents.length === 0) {
      alert('インポートするイベントを選択してください');
      return;
    }

    const unmappedEvents = selectedEvents.filter((event) => !projectMapping[event.id]);
    if (unmappedEvents.length > 0) {
      alert('選択したすべてのイベントにプロジェクトを割り当ててください');
      return;
    }

    setIsLoading(true);
    try {
      const { addTimestamp } = await import('@/lib/timeTracking');

      let successCount = 0;
      let errorCount = 0;

      for (const event of selectedEvents) {
        const projectId = projectMapping[event.id];
        const project = projects.find((p) => p.id === projectId);
        
        if (!project) {
          console.error(`Project not found: ${projectId}`);
          errorCount++;
          continue;
        }

        try {
          const startTime = new Date(event.start);
          const endTime = new Date(event.end);
          const diffMs = endTime.getTime() - startTime.getTime();
          const totalMinutes = Math.floor(diffMs / 60000);

          const { Timestamp } = await import('firebase/firestore');

          await addTimestamp(user.uid, {
            projectId: project.id,
            project: {
              id: project.id,
              name: project.name,
              code: project.code || '',
              client: project.client || null,
            },
            clockInTime: Timestamp.fromDate(startTime),
            clockOutTime: Timestamp.fromDate(endTime),
            totalMinutes,
            status: 'completed',
            memo: `Googleカレンダーからインポート: ${event.summary}`,
            categories: [],
            recordType: 'calendar',
          });

          successCount++;
        } catch (error) {
          console.error(`Failed to import event ${event.id}:`, error);
          errorCount++;
        }
      }

      alert(
        `インポート完了\n成功: ${successCount}件\n失敗: ${errorCount}件`
      );

      const successEventIds = new Set(
        selectedEvents
          .filter((event) => projectMapping[event.id])
          .map((event) => event.id)
      );
      
      setEvents((prev) => prev.filter((event) => !successEventIds.has(event.id)));
      
      const newMapping = { ...projectMapping };
      successEventIds.forEach((id) => delete newMapping[id]);
      setProjectMapping(newMapping);
    } catch (error) {
      console.error('Import failed:', error);
      alert('インポートに失敗しました: ' + error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Calendar className="w-8 h-8 text-blue-600" />
          Googleカレンダー連携
        </h1>
        <p className="text-gray-600 mt-2">
          Googleカレンダーのイベントを稼働記録としてインポートできます
        </p>
      </div>

      {/* 認証セクション */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">認証ステータス</h2>
        
        {isAuthorized ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-green-600">
              <CheckCircle2 className="w-6 h-6" />
              <span className="font-medium">Googleカレンダーに接続済み</span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchCalendarList}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                カレンダー一覧を取得
              </button>
              <button
                onClick={handleSignout}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                連携を解除
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Googleカレンダーと連携してイベントをインポートするには、まず認証が必要です。
            </p>
            <button
              onClick={handleAuthorize}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Googleカレンダーと連携
            </button>
          </div>
        )}
      </div>

      {/* カレンダー選択 */}
      {calendars.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">カレンダー選択</h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {calendars.map((calendar) => (
              <label
                key={calendar.id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedCalendars.has(calendar.id)}
                  onChange={() => handleCalendarToggle(calendar.id)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: calendar.backgroundColor }}
                ></div>
                <span className="text-gray-800">{calendar.summary}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* イベント取得 */}
      {calendars.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">イベント取得</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                開始日
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                終了日
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchEvents}
                disabled={isLoading || selectedCalendars.size === 0}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                <Clock className="w-4 h-4" />
                イベントを取得
              </button>
            </div>
          </div>
        </div>
      )}

      {/* イベントリスト */}
      {events.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-800">
                イベント一覧 ({events.length}件)
              </h2>
              <button
                onClick={handleSelectAllEvents}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {events.every((e) => e.selected) ? '全選択解除' : '全選択'}
              </button>
            </div>
            <button
              onClick={handleImport}
              disabled={isLoading || !events.some((e) => e.selected)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              選択したイベントをインポート ({events.filter((e) => e.selected).length}件)
            </button>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {events.map((event) => (
              <div
                key={event.id}
                className={`border rounded-lg p-4 transition-colors ${
                  event.selected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                  <div className="md:col-span-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={event.selected || false}
                      onChange={() => handleEventToggle(event.id)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </div>

                  <div className="md:col-span-5">
                    <h3 className="font-semibold text-gray-900 mb-2">{event.summary}</h3>
                    <p className="text-sm text-gray-600">
                      {new Date(event.start).toLocaleString('ja-JP')} 〜{' '}
                      {new Date(event.end).toLocaleString('ja-JP')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{event.calendarName}</p>
                  </div>

                  <div className="md:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      プロジェクトを選択
                    </label>
                    <select
                      value={projectMapping[event.id] || ''}
                      onChange={(e) =>
                        setProjectMapping({ ...projectMapping, [event.id]: e.target.value })
                      }
                      disabled={!event.selected}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    >
                      <option value="">プロジェクトを選択...</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name} {project.code ? `(${project.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
